import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Sparkles, Eye, EyeOff, CheckCircle } from 'lucide-react';
import api from '../lib/api';
import useWin98Scope from '../hooks/useWin98Scope';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  useWin98Scope();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="win-window w-full max-w-md">
          <div className="win-titlebar">
            <span>Reset Password</span>
          </div>
          <div className="p-6 text-center">
            <h2 className="text-xl font-semibold text-black mb-4">
              Invalid Reset Link
            </h2>
            <p className="text-black mb-6">
              This password reset link is invalid or has expired.
            </p>
            <Link to="/forgot-password">
              <Button className="win-btn-accent win-btn-default">
                Request New Link
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Brand mark on the desktop */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-3">
            <div className="w-12 h-12 bg-[var(--win-titlebar-start)] flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">KyberCosplay</h1>
          </div>
        </div>

        <div className="win-window">
          <div className="win-titlebar">
            <span>Reset Password</span>
          </div>
          <div className="p-6">
            {success ? (
              <div className="text-center">
                <div className="win-well w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-[var(--win-titlebar-start)]" />
                </div>
                <h2 className="text-xl font-semibold text-black mb-2">
                  Password Reset!
                </h2>
                <p className="text-black mb-6">
                  Your password has been successfully reset.
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="win-btn-accent win-btn-default font-semibold"
                  data-testid="go-to-login-btn"
                >
                  Go to Login
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-black mb-2">
                    Reset Password
                  </h2>
                  <p className="text-black">
                    Enter your new password below.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-black">New Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        data-testid="reset-password-input"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        className="h-12 pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-black"
                        style={{ border: 'none', boxShadow: 'none', background: 'transparent', padding: 0 }}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-black">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      data-testid="reset-confirm-password-input"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="h-12"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="reset-submit-btn"
                    disabled={loading}
                    className="w-full h-12 win-btn-accent win-btn-default font-semibold"
                  >
                    {loading ? 'Resetting…' : 'Reset Password'}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
